/**
 * ChatProgressWebSocket — connection manager for the chat progress event stream.
 *
 * Mirrors ProposalWebSocket's reconnect / heartbeat shape: exponential backoff
 * with jitter, application-level ping/pong heartbeat, and 4401/4403 auth-error
 * handling. Per-conversation filtering happens server-side; there is no
 * subscription protocol needed from the client.
 */

import { getWebSocketBaseUrl } from '@/lib/config';

import type {
  ChainCompletedEvent,
  ChainFailedEvent,
  ChatProgressClientMessage,
  ChatProgressServerMessage,
  ChatProgressWebSocketStatus,
  StepProgressEvent,
  TitleUpdatedEvent,
} from './types';

interface ChatProgressWebSocketConfig {
  orgId: string;
  conversationId: string;
  onStepProgress: (event: StepProgressEvent) => void;
  onChainCompleted: (event: ChainCompletedEvent) => void;
  onChainFailed: (event: ChainFailedEvent) => void;
  onTitleUpdated: (event: TitleUpdatedEvent) => void;
  onStatusChange?: (status: ChatProgressWebSocketStatus) => void;
}

/** Reconnect backoff settings (matches CrmWebSocket so users see consistent recovery). */
const MIN_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;

/** Heartbeat — application-level ping/pong; the JS API can't send protocol pings. */
const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_TIMEOUT_MS = 10000;

/** Add ±20% jitter to a delay to avoid thundering-herd on backend restart. */
function withJitter(delay: number): number {
  const jitter = 0.8 + Math.random() * 0.4;
  return Math.round(delay * jitter);
}

export class ChatProgressWebSocket {
  private ws: WebSocket | null = null;
  private config: ChatProgressWebSocketConfig;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private status: ChatProgressWebSocketStatus = 'disconnected';
  private destroyed = false;
  private shouldNotReconnect = false;

  constructor(config: ChatProgressWebSocketConfig) {
    this.config = config;
  }

  connect(): void {
    if (this.destroyed || this.shouldNotReconnect) {
      return;
    }
    this.disconnect();
    this.setStatus('connecting');

    const url = `${getWebSocketBaseUrl()}/ws/orgs/${this.config.orgId}/conversations/${this.config.conversationId}/progress/`;

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.warn(
        '[ChatProgressWebSocket] WebSocket constructor failed, scheduling reconnect:',
        err
      );
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      if (this.destroyed) {
        return;
      }
      this.setStatus('connected');
      this.reconnectAttempt = 0;
      this.startHeartbeat();
    };

    this.ws.onmessage = event => {
      if (this.destroyed) {
        return;
      }
      // Any inbound message means the server is alive — clear pending timeout.
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }
      try {
        const message = JSON.parse(event.data) as ChatProgressServerMessage;
        this.handleMessage(message);
      } catch {
        console.error(
          '[ChatProgressWebSocket] Failed to parse message:',
          event.data
        );
      }
    };

    this.ws.onclose = closeEvent => {
      if (this.destroyed) {
        return;
      }
      this.stopHeartbeat();
      this.setStatus('disconnected');

      // Auth/permission errors — don't reconnect; we'll just fail forever.
      if (closeEvent.code === 4401 || closeEvent.code === 4403) {
        console.warn(
          `[ChatProgressWebSocket] Connection rejected (code ${closeEvent.code}), not reconnecting`
        );
        this.shouldNotReconnect = true;
        return;
      }

      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      // onerror is always followed by onclose, where reconnect is scheduled.
    };
  }

  disconnect(): void {
    this.clearReconnectTimer();
    this.stopHeartbeat();

    if (this.ws) {
      // Drop handlers so the deliberate close doesn't trigger reconnect.
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.onopen = null;

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'Client disconnect');
      }
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  destroy(): void {
    this.destroyed = true;
    this.disconnect();
  }

  getStatus(): ChatProgressWebSocketStatus {
    return this.status;
  }

  // --- Internal ---

  private handleMessage(message: ChatProgressServerMessage): void {
    switch (message.type) {
      case 'step.progress':
        this.config.onStepProgress(message);
        break;
      case 'chain.completed':
        this.config.onChainCompleted(message);
        break;
      case 'chain.failed':
        this.config.onChainFailed(message);
        break;
      case 'title.updated':
        this.config.onTitleUpdated(message);
        break;
      case 'pong':
        break;
    }
  }

  private send(message: ChatProgressClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private setStatus(status: ChatProgressWebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.config.onStatusChange?.(status);
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed || this.shouldNotReconnect) {
      return;
    }
    this.clearReconnectTimer();
    const delay = withJitter(
      Math.min(
        MIN_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempt,
        MAX_RECONNECT_DELAY_MS
      )
    );
    this.reconnectAttempt++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' });
      // Re-arm the dead-connection timeout. Clearing first prevents leaked
      // timers after sleep/wake when queued timers can fire out of order.
      if (this.heartbeatTimeoutTimer) {
        clearTimeout(this.heartbeatTimeoutTimer);
        this.heartbeatTimeoutTimer = null;
      }
      this.heartbeatTimeoutTimer = setTimeout(() => {
        console.warn('[ChatProgressWebSocket] Heartbeat timeout, reconnecting');
        this.ws?.close();
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.heartbeatTimeoutTimer) {
      clearTimeout(this.heartbeatTimeoutTimer);
      this.heartbeatTimeoutTimer = null;
    }
  }
}
